import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router'
import ReactCountryFlag from 'react-country-flag'
import { FaSearch, FaTrash } from 'react-icons/fa'
import { Box, Stack, Heading, Text, InputGroup, Input, Icon, Button, SimpleTable, Badge, EmptyBox, ConfirmButton } from '@/components/lib'
import api from '../../../api'
import utils from '../../../utils/utils'
import useStore from '../../../store'
import useTitle from '../../../hooks/useTitle'

import countryCodes from '../../../utils/country_codes.json'
import { IRB_TYPES, APPROVAL_STATUSES, ACTIVITY_STATUSES } from './New'

export default function Sites () {
  const [filter, setFilter] = useState('')
  const { id } = useParams()
  const { user, team, trial, updateTrial, sites, addSite, deleteSite } = useStore()
  useTitle('Sites', trial)

  useEffect(() => {
    api.sites.getForTrial(id, d => d.sites?.forEach(s => addSite(s)))
  }, [id])

  function onDeleteSite (siteId) {
    api.sites.delete(siteId, () => {
      utils.toast('success', 'Site deleted')
      deleteSite(siteId)
      updateTrial(trial._id, { siteCount: (trial.siteCount ?? 1) - 1 })
    }, err => utils.toast('error', 'Unable to delete site', err.message))
  }

  function getCountryCodeForName (country) {
    const code = countryCodes.find(c => c.name === country)?.code
    return code
  }

  const filteredSites = filter ? sites.filter(s => s.id?.toLowerCase()?.includes(filter) || s.name?.toLowerCase()?.includes(filter) || s.addressCity?.toLowerCase()?.includes(filter)) : sites

  return (
    <Stack gap={5}>
      <Box display='flex' justifyContent='space-between'>
        <Box>
          <Heading as='h2' size='lg'>Trial sites</Heading>
          <Text>Physical or virtual locations of the trial.</Text>
        </Box>
        <Button as={Link} to={`/trials/${trial._id}/sites/new`} colorPalette='primary'>Create a new site</Button>
      </Box>

      {(sites?.length > 0)
        ? (
          <>
            <InputGroup
              size='sm' maxW={300}
              leftIcon={<Icon as={FaSearch} color='fg.muted' />}
            >
              <Input size='sm' placeholder='Type to search your sites...' value={filter} onChange={e => setFilter(e.target.value.toLowerCase())} />
            </InputGroup>

            <SimpleTable
              asCard
              tableProps={{ variant: 'striped', size: 'sm' }}
              headers={['', 'ID', 'Name', 'Location', 'IRB/EC Type', 'Approval', 'Activity', 'Issues', '']}
              rows={filteredSites.map(site => {
                const approvalStatus = APPROVAL_STATUSES.find(s => s.key === site.approvalStatus)
                const activityStatus = ACTIVITY_STATUSES.find(s => s.key === site.activityStatus)
                const irbType = IRB_TYPES.find(s => s.key === site.irb)
                return [
                  <Box key={site._id}>
                    {site.imageUrl &&
                      <Box backgroundImage={site.imageUrl} rounded='md' backgroundSize='cover' w='40px' h='40px' />}
                  </Box>,
                  <Text key={site._id}>{site.id}</Text>,
                  <Text key={site._id}>{site.name}</Text>,
                  <Stack direction='row' alignItems='center' key={site._id}>
                    <ReactCountryFlag countryCode={getCountryCodeForName(site.addressCountry)} svg />
                    {site.addressCity}
                  </Stack>,
                  <Badge key={site._id}>{irbType?.name}</Badge>,
                  <Badge key={site._id} colorPalette={approvalStatus?.color}>{approvalStatus?.name}</Badge>,
                  <Badge key={site._id} colorPalette={activityStatus?.color}>{activityStatus?.name}</Badge>,
                  site.issueCount,
                  <Stack key={site._id} direction='row' justifyContent='end'>
                    {utils.hasFeature(team, 'siteMonitoring') &&
                      <Button size='sm' colorPalette='primary' as={Link} to={`/trials/${id}/sites/${site._id}/monitoring`}>Monitoring</Button>}
                    <Button size='sm' colorPalette='primary' variant='outline' as={Link} to={`/trials/${id}/sites/${site._id}`}>Edit</Button>
                    {utils.canManageTrial(user, trial) &&
                      <ConfirmButton
                        trigger={<Button size='sm' variant='ghost'><Icon><FaTrash /></Icon></Button>}
                        header='Really delete this site?'
                        footer='Any participants assigned to this site will be removed from the site.'
                        onConfirm={e => onDeleteSite(site._id)}
                      />}
                  </Stack>
                ]
              })}
            />
          </>)
        : (
          <EmptyBox icon='site' title='Site management' description='Sites allow you to separate personnel and participants by geographic (or virtual) location. For example, in distributed trials you might work with a number of clinics or centres to which your participants will attend.'>
            <Button colorPalette='primary' as={Link} to={`/trials/${trial._id}/sites/new`}>Create your first site</Button>
          </EmptyBox>)}
    </Stack>
  )
}
